import uuidv4 from 'uuid/v4';
import actionTypes from 'constants/action-types';
import { createRoutine } from 'redux-saga-routines';
import { CHANGE_PASSWORD_DIALOG_STATES } from 'constants/dialog';

/**
 * Action creator for setting individual or groups of fields in the change password dialog.
 *
 * The reducer for this action will take the current state of the dialog, and then overwrite
 * the existing fields with the state parameter from this action. This is to make it possible
 * to have sagas drive the change password dialog, such as with stitch errors etc.
 *
 * @param {Object} state - state to apply to the change password dialog
 * @return {Object} - action object
 */
export function setChangePasswordDialog(state) {
  return {
    type: actionTypes.DIALOG.CHANGE_PASSWORD,
    payload: state,
  };
}

export function openUserChangePasswordDialog(user) {
  return {
    type: actionTypes.DIALOG.CHANGE_PASSWORD,
    payload: {
      user,
      isOpen: CHANGE_PASSWORD_DIALOG_STATES.USER,
      passwords: null,
      validationMessage: '',
    },
  };
}

export function openAdminChangePasswordDialog(user) {
  return {
    type: actionTypes.DIALOG.CHANGE_PASSWORD,
    payload: {
      user,
      isOpen: CHANGE_PASSWORD_DIALOG_STATES.ADMIN,
      passwords: null,
      validationMessage: '',
    },
  };
}

export function closeChangePasswordDialog() {
  return {
    type: actionTypes.DIALOG.CHANGE_PASSWORD,
    payload: {
      isOpen: CHANGE_PASSWORD_DIALOG_STATES.NONE,
    },
  };
}

export function openEditProfileDialog(user) {
  return {
    type: actionTypes.DIALOG.EDIT_PROFILE,
    payload: {
      user,
      isOpen: true,
      errorMessage: '',
      lastName: user.lastName,
      firstName: user.firstName,
    },
  };
}

export function closeEditProfileDialog() {
  return {
    type: actionTypes.DIALOG.EDIT_PROFILE,
    payload: { isOpen: false },
  };
}

export function editProfile(profile) {
  return {
    type: actionTypes.DIALOG.EDIT_PROFILE,
    payload: profile,
  };
}

/**
 * Opens the AutoRefresh dialog, and sets the ID of the item which we want to update.
 * Interval is null since the dialog shouldn't have a set interval yet.
 * @param {string} _id - ID of the item which we want to set autorefresh for
 * @returns {object} - action object
 */
export function openAutoRefreshDialog(_id) {
  return {
    type: actionTypes.DIALOG.AUTO_REFRESH,
    payload: { isOpen: true, _id, interval: null },
  };
}

/**
 * Closes the AutoRefresh dialog
 * @returns {object} - action object
 */
export function closeAutoRefreshDialog() {
  return {
    type: actionTypes.DIALOG.AUTO_REFRESH,
    payload: { isOpen: false, _id: null },
  };
}

/**
 * Sets the selected interval for the auto refresh
 * Done this way since we don't want to persist the chosen time accidentally
 * @param {number} interval - time in milliseconds which we want to set an interval for
 * @returns {object} - action object
 */
export function setAutoRefreshDialogInterval(interval) {
  return {
    type: actionTypes.DIALOG.AUTO_REFRESH,
    payload: { interval },
  };
}

/**
 * Action creator for opening the 'move to' dialog
 *
 * @param {String} dashboardItemId - _id of the dashboard item to move.
 * @param {String} sourceDashboardId - _id of the source dashboard to move from.
 *
 * @return {Object} - action object
 */
export const openMoveDashboardItemDialog = (
  dashboardItemId,
  sourceDashboardId
) => {
  return {
    type: actionTypes.DIALOG.MOVE_DASHBOARD_ITEM,
    payload: { isOpen: true, dashboardItemId, sourceDashboardId },
  };
};

/**
 * Action creator for closing the 'move to' dialog
 *
 * @return {Object} - action object
 */
export const closeMoveDashboardItemDialog = () => {
  return {
    type: actionTypes.DIALOG.MOVE_DASHBOARD_ITEM,
    payload: {
      isOpen: false,
      dashboardItemId: '',
      sourceDashboardId: '',
      targetDashboardId: '',
      isMoving: false,
    },
  };
};

/**
 * Action creator for setting the dashboard id the 'move to' dialog
 *
 * @param {String} targetDashboardId - _id of the target dashboard to move to.
 * @return {Object} - action object
 */
export const setMoveDashboardItemDialog = targetDashboardId => {
  return {
    type: actionTypes.DIALOG.MOVE_DASHBOARD_ITEM,
    payload: { targetDashboardId },
  };
};

/**
 * Action creator for opening the 'Embed chart' dialog
 *
 * @param {Object} item - dashboard item to be embedded.
 * @return {Object} - action object
 */
export const openEmbedDashboardItemDialog = item => {
  const { _id: dashboardItemId, embedding, dataSourceId } = item;
  return {
    type: actionTypes.DIALOG.EMBED_DASHBOARD_ITEM.OPEN,
    payload: {
      isOpen: true,
      dataSourceId,
      dashboardItemId,
      embedding: embedding && embedding.id ? embedding : { id: uuidv4() },
    },
  };
};

/**
 * Action creator for closing the 'Embed chart' dialog
 *
 * @return {Object} - action object
 */
export const closeEmbedDashboardItemDialog = () => {
  return {
    type: actionTypes.DIALOG.EMBED_DASHBOARD_ITEM.CLOSE,
    payload: { isOpen: false, isSaving: false },
  };
};

/**
 * Fetch fields to help with specifying ad-hoc filter fields in the EmbedDashboardItemDailog.
 */
export const sampleDataSourceForEmbedding = createRoutine(
  actionTypes.DIALOG.EMBED_DASHBOARD_ITEM.FETCH_FIELDS
);

/**
 * Save fields specified by user that can be used for ad-hoc filter.
 */
export const saveWhitelistedFields = createRoutine(
  actionTypes.DIALOG.EMBED_DASHBOARD_ITEM.SAVE_FIELDS
);

/**
 * Opens delete dashboard item dialog.
 *
 * @param {String} dashboardItemId - The _id of the item to delete
 * @param {String} dashboardId - The _id of the dashboard item is being deleted
 *
 * @return {Object} - action object
 **/
export function openDeleteDashboardItemDialog(dashboardItemId, dashboardId) {
  return {
    type: actionTypes.DIALOG.DELETE_DASHBOARD_ITEM,
    payload: { isOpen: true, dashboardItemId, dashboardId },
  };
}

/**
 * Closes delete dashboard item dialog.
 *
 * @return {Object} - action object
 **/
export function closeDeleteDashboardItemDialog() {
  return {
    type: actionTypes.DIALOG.DELETE_DASHBOARD_ITEM,
    payload: { isOpen: false, dashboardItemId: '', dashboardId: '' },
  };
}

/**
 * Action creator for changing embedding option of a data source.
 *
 * @param {Object} payload payload object
 * @return {Object} - action object
 **/
export function changeDataSourceEmbeddingOption(payload) {
  return {
    type: actionTypes.DIALOG.DATA_SOURCE_EMBEDDING_OPTION,
    payload,
  };
}

/**
 * Action creator for changing embedding option of a data source.
 *
 * @return {Object} - action object
 **/
export function closeDataSourceEmbeddingOption() {
  return {
    type: actionTypes.DIALOG.DATA_SOURCE_EMBEDDING_OPTION,
    payload: { isOpen: false, dataSourceId: null, allowEmbedding: null },
  };
}

/**
 * Action creator for opening the read preference of a data source
 * @param {Object} payload payload object
 * @return {Object} - action object
 */

export function openReadPreferenceDialog(payload) {
  return {
    type: actionTypes.DIALOG.DATA_SOURCE_READ_PREFERENCE,
    payload: { ...payload, isOpen: true, errorMessage: '' },
  };
}

/**
 * Action creator for changing the read preference of a data source
 * @param {Object} payload payload object
 * @return {Object} - action object
 */

export function changeReadPreferenceDialog(payload) {
  return {
    type: actionTypes.DIALOG.DATA_SOURCE_READ_PREFERENCE,
    payload,
  };
}

/**
 * Action creator for closing the read preference dialog of a data source
 * @return {Object} - action object
 */

export function closeReadPreferenceDialog() {
  return {
    type: actionTypes.DIALOG.DATA_SOURCE_READ_PREFERENCE,
    payload: { isOpen: false, stitchServiceName: null, stitchServiceId: null },
  };
}

export function openDeleteDataSourcePipelineDialog(dataSourceId) {
  return {
    type: actionTypes.DIALOG.DELETE_DATASOURCE_PIPELINE.OPEN,
    payload: { dataSourceId, errorMessage: '' },
  };
}

export function closeDeleteDataSourcePipelineDialog() {
  return {
    type: actionTypes.DIALOG.DELETE_DATASOURCE_PIPELINE.CLOSE,
    payload: { dataSourceId: '' },
  };
}

export function openChartDataViewDialog() {
  return {
    type: actionTypes.DIALOG.VIEW_CHART_DATA,
    payload: { isOpen: true },
  };
}

export function closeChartDataViewDialog() {
  return {
    type: actionTypes.DIALOG.VIEW_CHART_DATA,
    payload: { isOpen: false },
  };
}

export function openViewChartPipelineDialog() {
  return {
    type: actionTypes.DIALOG.VIEW_CHART_PIPELINE,
    payload: { isOpen: true },
  };
}

export function closeViewChartPipelineDialog() {
  return {
    type: actionTypes.DIALOG.VIEW_CHART_PIPELINE,
    payload: { isOpen: false },
  };
}

export function openChartDocumentsDialog() {
  return {
    type: actionTypes.DIALOG.VIEW_CHART_DOCUMENTS,
    payload: { isOpen: true },
  };
}

export function closeChartDocumentsDialog() {
  return {
    type: actionTypes.DIALOG.VIEW_CHART_DOCUMENTS,
    payload: { isOpen: false },
  };
}

export function openDeleteDataSourceDialog(dataSourceId) {
  return {
    type: actionTypes.DIALOG.DELETE_DATASOURCE.OPEN,
    payload: { dataSourceId, errorMessage: '' },
  };
}

export function closeDeleteDataSourceDialog() {
  return {
    type: actionTypes.DIALOG.DELETE_DATASOURCE.CLOSE,
    payload: { dataSourceId: '' },
  };
}

export const autoRefreshDialogActions = {
  openAutoRefreshDialog,
  closeAutoRefreshDialog,
  setAutoRefreshDialogInterval,
};

export const moveDashboardItemActions = {
  openMoveDashboardItemDialog,
  closeMoveDashboardItemDialog,
  setMoveDashboardItemDialog,
};

export const embedDashboardItemActions = {
  openEmbedDashboardItemDialog,
  closeEmbedDashboardItemDialog,
  sampleDataSourceForEmbedding,
  saveWhitelistedFields,
};

export const deleteDashboardItemActions = {
  openDeleteDashboardItemDialog,
  closeDeleteDashboardItemDialog,
};

export const deleteDataSourcePipelineActions = {
  openDeleteDataSourcePipelineDialog,
  closeDeleteDataSourcePipelineDialog,
};

export const deleteDataSourceActions = {
  openDeleteDataSourceDialog,
  closeDeleteDataSourceDialog,
};

export default {
  editProfile,
  openEditProfileDialog,
  closeEditProfileDialog,
  closeChangePasswordDialog,
  openAdminChangePasswordDialog,
  openUserChangePasswordDialog,
  setChangePasswordDialog,
  openChartDataViewDialog,
  closeChartDataViewDialog,
  ...autoRefreshDialogActions,
  ...moveDashboardItemActions,
  ...embedDashboardItemActions,
  ...deleteDashboardItemActions,
  ...deleteDataSourcePipelineActions,
  ...deleteDataSourceActions,
};
